import React from 'react';
import { screenPx } from '../../../helpers/handleScale';
import {
  HANDLE_STROKE_WIDTH_PX,
  ROUND_HANDLE_RADIUS_PX,
  SQUARE_HANDLE_SIZE_PX,
} from '../../../helpers/handleTokens';

/**
 * The shared handle primitives. Every visible handle on the canvas is one of these, so
 * that size, outline weight and colour are decided in one place.
 *
 * Two things these deliberately do NOT do, because the interaction layer depends on
 * them (see context/MouseEvents/utils/typeGuards.ts):
 *  - `id` is passed straight through, never derived or prefixed. Handles have no type;
 *    the id string is what identifies them.
 *  - no wrapper element is added around the shape, so the event target stays the shape
 *    itself.
 */

/**
 * Outline weight is applied as a CSS custom property rather than a `stroke-width`
 * attribute on purpose: an SVG presentation attribute loses to any class rule, so a
 * `strokeWidth` prop next to `className="handle"` would be silently discarded. A
 * custom property set inline wins, so the computed value is the one we pass here.
 */
const strokeWidthStyle = (
  scale: number,
  strokeWidthPx: number = HANDLE_STROKE_WIDTH_PX
): React.CSSProperties =>
  ({
    ['--handle-stroke-width' as string]: screenPx(strokeWidthPx, scale),
  }) as React.CSSProperties;

type HandleProps = {
  id?: string;
  cx: number;
  cy: number;
  scale: number;
  cursor?: string;
  className?: string;
  interactive?: boolean;
  'data-testid'?: string;
};

/**
 * Square handle, centred on (cx, cy). Used for element corners, polyline vertices,
 * multiselect corners and the split button.
 */
export const SquareHandle = ({
  id,
  cx,
  cy,
  scale,
  cursor,
  className,
  interactive = true,
  sizePx = SQUARE_HANDLE_SIZE_PX,
  strokeWidthPx,
  ...rest
}: HandleProps & { sizePx?: number; strokeWidthPx?: number }) => {
  const size = screenPx(sizePx, scale);
  const half = size / 2;

  return (
    <rect
      id={id}
      width={size}
      height={size}
      x={cx - half}
      y={cy - half}
      className={['handle', className].filter(Boolean).join(' ')}
      style={{
        ...strokeWidthStyle(scale, strokeWidthPx),
        ...(cursor ? { cursor } : {}),
        ...(interactive ? {} : { pointerEvents: 'none' as const }),
      }}
      {...rest}
    />
  );
};

/**
 * Round handle, centred on (cx, cy). Used for the triangle apex and corner-radius
 * handles.
 */
export const RoundHandle = ({
  id,
  cx,
  cy,
  scale,
  cursor,
  className,
  interactive = true,
  radiusPx = ROUND_HANDLE_RADIUS_PX,
  onMouseEnter,
  onMouseLeave,
  ...rest
}: HandleProps & {
  radiusPx?: number;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}) => (
  <circle
    id={id}
    r={screenPx(radiusPx, scale)}
    cx={cx}
    cy={cy}
    className={['handle', className].filter(Boolean).join(' ')}
    style={{
      ...strokeWidthStyle(scale),
      ...(cursor ? { cursor } : {}),
      ...(interactive ? {} : { pointerEvents: 'none' as const }),
    }}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    {...rest}
  />
);

/**
 * Invisible, oversized hit target sitting under a visible handle.
 *
 * `id` is required and has no default: several call sites deliberately pass the *bare
 * element id* rather than the handle id, so that a click on the wide hit area falls
 * through to selecting the element while only the small visible handle starts a drag.
 */
export const HandleHitArea = ({
  id,
  cx,
  cy,
  radius,
  cursor,
  ...rest
}: {
  id: string;
  cx: number;
  cy: number;
  radius: number;
  cursor?: string;
  'data-testid'?: string;
}) => (
  <circle
    id={id}
    cx={cx}
    cy={cy}
    r={radius}
    fill="transparent"
    pointerEvents="all"
    style={cursor ? { cursor } : undefined}
    {...rest}
  />
);

/** Invisible thick line used as the hit target for a polyline segment. */
export const HandleHitLine = ({
  id,
  x1,
  y1,
  x2,
  y2,
  strokeWidth,
}: {
  id: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  strokeWidth: number;
}) => (
  <line
    id={id}
    x1={x1}
    y1={y1}
    x2={x2}
    y2={y2}
    stroke="transparent"
    strokeWidth={strokeWidth}
    style={{ pointerEvents: 'stroke' }}
  />
);
